import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { KnBrShippingDetailsComponent } from './kn-br-shipping-details/kn-br-shipping-details.component';
import { KnBrReviewOrderComponent } from './kn-br-review-order/kn-br-review-order.component';
import { IconComponent } from '@spartacus/storefront';
import { KnBrSalesCheckoutService } from 'src/app/services/kn-br-sales-checkout.service';
import { finalize, Observable, Subject, switchMap, filter, takeUntil, tap, debounceTime, distinctUntilChanged } from 'rxjs';
import { GlobalMessageService, GlobalMessageType, RoutingService } from '@spartacus/core';
import { KnBrCommonService } from 'src/app/services/kn-br-common.service';
import { FormsModule } from '@angular/forms';
import { ActiveCartService } from '@spartacus/cart/base/core';
import { MultiCartFacade } from '@spartacus/cart/base/root';
import { KnBrCartContextService } from 'src/app/store/kn-br-cart-context/kn-br-cart-context.service';

@Component({
  selector: 'knbr-kn-br-sales-checkout',
  standalone: true,
  imports: [CommonModule, IconComponent, FormsModule, KnBrShippingDetailsComponent, KnBrReviewOrderComponent],
  templateUrl: './kn-br-sales-checkout.component.html',
  styleUrls: ['./kn-br-sales-checkout.component.scss'],
})
export class KnBrSalesCheckoutComponent implements OnInit, OnDestroy {

  currentStep = 1;
  shippingSubmitted = false;
  private destroy$ = new Subject<void>();
  poNumber: string = '';
  poNumberError = '';
  cartId: string = "";
  orderemail:string="";

  // ✓ Subject for debounced PO number updates
  private poNumberSubject$ = new Subject<string>();

  @ViewChild(KnBrShippingDetailsComponent)
  shippingDetailsComponent!: KnBrShippingDetailsComponent;

  constructor(private KnBrCheckoutService: KnBrSalesCheckoutService,
    protected globalMessageService: GlobalMessageService,
    private routingService: RoutingService,
    protected commonService: KnBrCommonService,
    protected activeCartService: ActiveCartService,
    private multiCartFacade: MultiCartFacade,
    private knBrCartContextService: KnBrCartContextService,
    private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    // ✓ Setup debounced PO Number updates (500ms delay)
    this.poNumberSubject$
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((poNumber) => {
        this.savePONumber(poNumber);
      });

    this.knBrCartContextService.get$
      .pipe(
        takeUntil(this.destroy$),
        filter((cartId) => !!cartId),
        tap((cartId) => {
          this.cartId = cartId;
          this.multiCartFacade.reloadCart(cartId);
        }),
        switchMap((cartId) => this.multiCartFacade.getCart(cartId)),
        filter((cart) => !!cart)
      )
      .subscribe(
        (cart) => {
          this.poNumber = cart.purchaseOrderNumber || '';
          this.orderemail = cart?.deliveryAddress?.email || '';
          this.cdr.detectChanges();
        },
        (error) => {
          console.error('Error loading cart in checkout:', error);
        }
      );
  }

  goToStep(step: number) {
    if (step === 1) {
      this.shippingSubmitted = false;
      this.currentStep = 1;
      return;
    }

    if (step === 2 && this.canOpenReview()) {
      this.currentStep = 2;
    }
  }

  togglePanel(event: Event, step: number) {
    event.stopPropagation();
    if (this.currentStep === step) {
      this.currentStep = 0;
    } else {
      this.goToStep(step);
    }
  }

  submitShippingDetails() {
    if (this.shippingDetailsComponent.shippingForm.invalid) {
      this.shippingDetailsComponent.shippingForm.markAllAsTouched();
      this.currentStep = 1;
      return;
    }
    else {
      this.commonService.showSpinner$.next(true);
      const { submitterEmail, firstName, lastName, company, street1, street2, city, state, postal, country, phone, extension, overnight, plantLocation, comments } = this.shippingDetailsComponent.shippingForm.value;
      const cleanedPhoneNumber = phone.replace(/\D/g, "");
      const plantLocationName = this.shippingDetailsComponent.labelsPlantName;
      const shippingPayload = {
        email: submitterEmail,
        firstName: firstName,
        lastName: lastName,
        companyName: company,
        line1: street1,
        line2: street2,
        town: city,
        region: {
          isocode: state
        },
        postalCode: postal,
        country: {
          isocode: country
        },
        phone: cleanedPhoneNumber,
        extension: extension,
        labelsPlantId : plantLocation,
        note: comments,
        labelsPlantName:plantLocationName
      }

      this.KnBrCheckoutService.shippingDetails(shippingPayload)
        .pipe(
          switchMap(() => this.setDeliveryAddress()),
          finalize(() => {
            this.commonService.showSpinner$.next(false);
          }),
          takeUntil(this.destroy$)
        )
        .subscribe({
            next: () => {
              this.shippingSubmitted = true;
              this.multiCartFacade.reloadCart(this.cartId);
              if (this.currentStep === 1) {
                this.currentStep = 2;
                this.cdr.detectChanges();
                window.scrollTo(0, 0);
              }
              this.globalMessageService.add(
                'Shipping details updated successfully.',
                GlobalMessageType.MSG_TYPE_CONFIRMATION
              );
            },
            error: () => {
              this.shippingSubmitted = false;
              this.globalMessageService.add(
                'Failed to submit Shipping Details.',
                GlobalMessageType.MSG_TYPE_ERROR
              );
            }
          });
    }
  }

  private setDeliveryAddress(): Observable<any> {
    const {
      billingName,
      billingAddr2,
      billingAddr1,
      billingCountry,
      billingCity,
      overnight,
      billingState,
      overnightAccountNumber,
      overnightCarrier,
      billingPostal
    } = this.shippingDetailsComponent.shippingForm.value;

    let payload = {
      line2: billingAddr2,
      line1: billingAddr1,
      city: billingCity,
      postalCode: billingPostal,
      carrierChargerName: billingName,
      overnightShipping: overnight,
      overnightCarrier: overnightCarrier,
      overnightAccountNumber: overnightAccountNumber
    };
    if (billingCountry && billingCountry !== '') {
      payload['country'] = {
        isocode: billingCountry
      }
    }
    if (billingState && billingState !== '') {
      payload['region'] = {
        isocode: billingState
      }
    }
    return this.KnBrCheckoutService.setDeliveryAddress(payload);
  }

  editShipping(event: Event): void {
    event.stopPropagation();
    this.shippingSubmitted = false;
    this.currentStep = 1;
  }

  previous() {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  get canPlaceOrder(): boolean {
    return this.shippingSubmitted && this.shippingDetailsComponent?.shippingForm.valid === true;
  }

  onPoNumberInput(event: Event): void {
    this.poNumber = (event.target as HTMLInputElement).value;
    this.poNumberError = '';
    this.cdr.detectChanges();
    // ✓ Emit to subject (will be debounced)
    this.poNumberSubject$.next(this.poNumber);
  }

  private canOpenReview(): boolean {
    const shippingForm = this.shippingDetailsComponent.shippingForm;
    if (shippingForm.invalid) {
      shippingForm.markAllAsTouched();
      this.globalMessageService.add(
        'Please submit Shipping Details.',
        GlobalMessageType.MSG_TYPE_WARNING
      );
      this.currentStep = 1;
      return false;
    }
    else if (!this.shippingSubmitted) {
      this.globalMessageService.add(
        'Please submit Shipping Details.',
        GlobalMessageType.MSG_TYPE_WARNING
      );
      this.currentStep = 1;
      return false;
    }
    else {
      return true;
    }
  }

  savePONumber(poNumber?: string): void {
    // ✓ Use passed parameter or current value
    const valueToSave = poNumber !== undefined ? poNumber : this.poNumber;
    const trimmedPONumber = valueToSave?.trim() || '';
    this.commonService.showSpinner$.next(true);
    this.KnBrCheckoutService.updatePONumber(trimmedPONumber).subscribe({
      next: () => {
        this.commonService.showSpinner$.next(false);
        this.poNumber = trimmedPONumber;
        this.poNumberError = '';
      },
      error: (error) => {
        this.commonService.showSpinner$.next(false);
        this.poNumberError = error?.error?.errors?.[0]?.message || 'Unable to save PO/Job Number.';
      }
    });
  }

  placeOrder(): void {
    if (!this.canPlaceOrder) {
      this.globalMessageService.add(
        'Please complete the shipping details before placing the order.',
        GlobalMessageType.MSG_TYPE_ERROR
      );
      return;
    }
    this.commonService.showSpinner$.next(true);
    this.KnBrCheckoutService.placeOrder()
      .pipe(
        finalize(() => {
          this.commonService.showSpinner$.next(false);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (data: any) => {
          this.globalMessageService.add(
            'Order placed successfully.',
            GlobalMessageType.MSG_TYPE_CONFIRMATION
          );
          this.KnBrCheckoutService.setOrderConfirmData(data);
          this.knBrCartContextService.reset();
          this.routingService.goByUrl('/salesOrderConfirmation');
        },
        error: () => {
          this.globalMessageService.add(
            'Failed to place order.',
            GlobalMessageType.MSG_TYPE_ERROR
          );
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
